/** AI 평가 구성 — 클라이언트 요청/캐시 (학생 정보 없음, 키는 서버에만) */
import { supabase } from '../supabaseClient';
import { aiAccessErrorMessage, syncAiQuotaFromResponse } from '../services/aiQuotaService';

export type AiEvalStep = 'fields' | 'rubric' | 'sheet';
export type AiEvalAssessmentType = '서술형' | '논술형' | '실기' | '관찰' | '포트폴리오';
export type AiEvalDifficulty = '쉬움' | '보통' | '어려움';
export const AI_EVAL_ASSESSMENT_TYPES: AiEvalAssessmentType[] = ['서술형', '논술형', '실기', '관찰', '포트폴리오'];
export const AI_EVAL_DIFFICULTIES: AiEvalDifficulty[] = ['쉬움', '보통', '어려움'];

export type AiEvalOptions = { questionCount: number; difficulty: AiEvalDifficulty; assessmentType: AiEvalAssessmentType };

export type AiEvalContext = {
  subject: string;
  grade: string;
  unit: string;
  learning: string;
  learningSiblings: string[];
  /** 선택한 학습내용 전부(단원·학습내용·성취기준 코드) */
  lessons?: Array<{ unit: string; learning: string; codes: string[] }>;
  standards: Array<{ code: string; area: string; text: string; levels: string[] }>;
  levelLabels: string[];
  options: AiEvalOptions;
  fields?: { unit_text: string; evaluation_element_text: string; evaluation_method_text: string };
  rubric?: { task: string; items: Array<{ header: string; cells: string[] }> } | null;
  /** 사용자 기준안 양식: 채울 칸의 요소 타이틀 (없으면 기본 양식) */
  templateFields?: Array<{ id: string; title: string; kind: string }>;
};

export type AiEvalFieldsResult = { unit_text: string; evaluation_element_text: string; evaluation_method_text: string };
export type AiEvalRubricResult = {
  task_title: string;
  task: string;
  task_activities?: string[];
  method_tags?: string[];
  items: Array<{ header: string; cells: string[] }>;
  level_memos: string[];
  notice: string;
  example_answer_top?: string;
  example_answer_mid?: string;
  /** 사용자 양식 칸별 내용(templateFields 의 id 기준) */
  template_values?: Array<{ id: string; value: string }>;
};
export type AiEvalSheetSection = {
  type: 'heading' | 'text' | 'table' | 'answerBox' | 'html';
  text: string;
  rows: string[][];
  lines: number;
  /** answerBox 답란 형식 */
  answer_style?: 'lines' | 'grid' | 'dots' | 'draw' | 'short' | '';
  html: string;
  /** 문항 번호(1부터). 0 = 문항 밖(제목·자료·자기평가 등) */
  question: number;
  /** 문항 안 하위 부분 번호((1)(2), 〈조건〉/답란 묶음). 0 = 발문 */
  part: number;
};
export type AiEvalSheetResult = { title: string; sections: AiEvalSheetSection[] };
export type AiEvalRate = { remaining: number; max: number; windowMinutes: number } | null;

type ResultByStep = { fields: AiEvalFieldsResult; rubric: AiEvalRubricResult; sheet: AiEvalSheetResult };

const ERRORS: Record<string, string> = {
  AI_NOT_CONFIGURED: 'AI 키가 설정되지 않았습니다.',
  UNAUTHORIZED: '로그인이 필요합니다. 다시 로그인해 주세요.',
  FORBIDDEN: '현재 이용권에서는 AI 평가 구성을 사용할 수 없습니다.',
  RATE_LIMITED: '요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.',
  NO_STANDARDS: '먼저 성취기준을 선택해 주세요.',
  INVALID_STEP: '잘못된 요청입니다.',
};

function apiUrl(): string {
  const base = (process.env.REACT_APP_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';
  return `${base}/api/ai-evaluation-compose`;
}

/** 같은 (단계, 성취기준, 학습내용, 옵션, 앞 단계 결과) 요청은 세션 동안 재사용 — 비용 절감 */
const memCache = new Map<string, unknown>();
const SS_PREFIX = 'cp:ai-eval-compose:';

function cacheKey(step: AiEvalStep, ctx: AiEvalContext): string {
  const s = JSON.stringify({ step, ctx });
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return `${step}:${(h >>> 0).toString(36)}:${s.length}`;
}

export function getCachedAiEval<S extends AiEvalStep>(step: S, ctx: AiEvalContext): ResultByStep[S] | null {
  const key = cacheKey(step, ctx);
  if (memCache.has(key)) return memCache.get(key) as ResultByStep[S];
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + key);
    if (raw) {
      const v = JSON.parse(raw) as ResultByStep[S];
      memCache.set(key, v);
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function putCache(step: AiEvalStep, ctx: AiEvalContext, value: unknown) {
  const key = cacheKey(step, ctx);
  memCache.set(key, value);
  try {
    sessionStorage.setItem(SS_PREFIX + key, JSON.stringify(value));
  } catch {
    /* quota */
  }
}

async function post(body: unknown, signal?: AbortSignal) {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERRORS.UNAUTHORIZED);
  let res: Response;
  try {
    res = await fetch(apiUrl(), {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new Error('AI 서버에 연결하지 못했습니다.');
  }
  const json = (await res.json().catch(() => null)) as
    | { ok?: boolean; result?: unknown; error?: string; rate?: AiEvalRate }
    | null;
  syncAiQuotaFromResponse(json);
  return { res, json };
}

export async function fetchAiEvalStatus(): Promise<{ ok: boolean; rate: AiEvalRate; error?: string }> {
  try {
    const { json } = await post({ mode: 'status' });
    return { ok: Boolean(json?.ok), rate: json?.rate ?? null, error: json?.error };
  } catch (e) {
    return { ok: false, rate: null, error: e instanceof Error ? e.message : 'FAILED' };
  }
}

export async function requestAiEval<S extends AiEvalStep>(
  step: S,
  ctx: AiEvalContext,
  opts?: { signal?: AbortSignal; force?: boolean }
): Promise<{ result: ResultByStep[S]; rate: AiEvalRate; cached: boolean }> {
  if (!opts?.force) {
    const hit = getCachedAiEval(step, ctx);
    if (hit) return { result: hit, rate: null, cached: true };
  }
  const { res, json } = await post({ step, context: ctx }, opts?.signal);
  if (!json || !json.ok) {
    const code = json?.error || `HTTP_${res.status}`;
    throw new Error(aiAccessErrorMessage(code, json) ?? ERRORS[code] ?? `AI 평가 구성을 받지 못했습니다. (${code})`);
  }
  const result = json.result as ResultByStep[S];
  putCache(step, ctx, result);
  return { result, rate: json.rate ?? null, cached: false };
}
